import { useCallback, useEffect, useRef, useState } from 'react';
import clock from '~/assets/icons/clock.svg';
import cross from '~/assets/icons/cross.svg';
import { playSound, Sound } from '~/assets/sounds';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import {
	NumberplatePreview,
	NumberPlatePreviewSize,
} from '~/components/ui/NumberplatePreview';
import { Text } from '~/components/ui/Text';
import {
	formatTime,
	IPlayerVehicleListItem,
	PlayerVehicleSource,
} from '~/shared';
import {
	StyledContainer,
	StyledCrossButtonContainer,
	StyledFavoriteIcon,
	StyledTimerContainer,
	StyledVehicleName,
	StyledVehicleNameContainer,
} from './VehicleRow.styled';

interface IVehicleRowProps extends React.HTMLAttributes<HTMLDivElement> {
	selected: boolean;
	data: IPlayerVehicleListItem;
	vehicleExpiresAt?: number | null;
	onRowClick: () => void;
	onRowDoubleClick: () => void;
	onFavoriteClick: () => void;
	onCrossClick: () => void;
}

const VehicleRow = ({
	selected,
	data,
	vehicleExpiresAt,
	onRowClick,
	onRowDoubleClick,
	onFavoriteClick,
	onCrossClick,
	...props
}: IVehicleRowProps) => {
	const [hovered, setHovered] = useState(false);
	const [isTimerVisible, setIsTimerVisible] = useState(
		!!vehicleExpiresAt && vehicleExpiresAt > Date.now(),
	);
	const isFavoriteEnabled = data.source === PlayerVehicleSource.Owned;
	const isDestroyEnabled = data.source !== PlayerVehicleSource.Job;
	const favoriteRef = useRef<HTMLImageElement>(null);
	const timerTextRef = useRef<HTMLDivElement>(null);

	const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		playSound(Sound.ClickButton);
		if (isFavoriteEnabled && e.target === favoriteRef.current) {
			onFavoriteClick();
			return;
		}
		onRowClick();
	};

	const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
		playSound(Sound.ClickButton);
		if (isFavoriteEnabled && e.target === favoriteRef.current) {
			return;
		}

		onRowDoubleClick();
	};

	const handleMouseHover = () => setHovered(true);
	const handleMouseLeave = () => setHovered(false);

	const handleMouseMove = useCallback(() => {
		if (!hovered) {
			setHovered(true);
		}
	}, [hovered]);

	useEffect(() => {
		if (!vehicleExpiresAt) {
			setIsTimerVisible(false);
			return;
		}

		const update = () => {
			const diff = vehicleExpiresAt - Date.now();

			setIsTimerVisible(diff > 0);

			if (!timerTextRef.current) return;

			if (diff <= 0) {
				clearInterval(intervalId);
				return;
			}

			timerTextRef.current.textContent = formatTime(diff, '{hh}:{mm}');
		};

		update();
		const intervalId = setInterval(update, 1000);

		return () => clearInterval(intervalId);
	}, [vehicleExpiresAt]);

	return (
		<StyledContainer
			className={selected ? 'selected' : ''}
			onClick={handleClick}
			onMouseEnter={() => {
				playSound(Sound.LinkCursor);
				handleMouseHover();
			}}
			onMouseLeave={handleMouseLeave}
			onMouseMove={handleMouseMove}
			onDoubleClick={handleDoubleClick}
			{...props}
		>
			<StyledVehicleNameContainer $favoriteEnabled={isFavoriteEnabled}>
				{isFavoriteEnabled && (
					<StyledFavoriteIcon
						ref={favoriteRef}
						className={data.isFavorite ? 'active' : undefined}
					/>
				)}
				<StyledVehicleName $timer={isTimerVisible} $selected={selected}>
					{data.name}
				</StyledVehicleName>
			</StyledVehicleNameContainer>
			{isTimerVisible && (
				<StyledTimerContainer>
					<img src={clock} />
					<Text ref={timerTextRef}>
						{vehicleExpiresAt &&
							formatTime(
								Math.max(vehicleExpiresAt - Date.now(), 0),
								'{hh}:{mm}',
							)}
					</Text>
				</StyledTimerContainer>
			)}
			<NumberplatePreview
				numberPlate={data.numberPlate}
				dark={!hovered && !selected}
				size={NumberPlatePreviewSize.Size30}
			/>
			{data.isSpawned && isDestroyEnabled && (
				<StyledCrossButtonContainer>
					<Button
						color={ButtonColors.Default}
						height={ButtonHeight.Height30}
						style={{ maxWidth: 30 }}
						onClick={(e) => {
							e.stopPropagation();
							onCrossClick();
						}}
					>
						<img src={cross} width={14} height={14} />
					</Button>
				</StyledCrossButtonContainer>
			)}
		</StyledContainer>
	);
};

export { VehicleRow };
